/**
 * Blackboard - shared memory for behavior trees. Key-value store accessible by nodes.
 */

import * as THREE from 'three';

import type { BlackboardKey } from './BlackboardKey.js';
import type { Actor } from '../../../actors/Actor.js';

/** Shared memory for behavior trees. Stores key-value pairs accessible by any node. */
export class Blackboard {
  private data: Map<string, any> = new Map();
  private owner: Actor | null = null;

  constructor(owner?: Actor) {
    this.owner = owner ?? null;
  }

  public setValue<T>(key: BlackboardKey<T> | string, value: T): void {
    const keyName = typeof key === 'string' ? key : key.name;
    this.data.set(keyName, value);
  }

  public getValue<T>(key: BlackboardKey<T> | string): T | undefined {
    const keyName = typeof key === 'string' ? key : key.name;
    const value = this.data.get(keyName);

    if (value === undefined && typeof key !== 'string' && key.defaultValue !== undefined) {
      return key.defaultValue;
    }

    return value;
  }

  public hasValue(key: BlackboardKey<any> | string): boolean {
    const keyName = typeof key === 'string' ? key : key.name;
    return this.data.has(keyName);
  }

  public removeValue(key: BlackboardKey<any> | string): void {
    const keyName = typeof key === 'string' ? key : key.name;
    this.data.delete(keyName);
  }

  public clear(): void {
    this.data.clear();
  }

  public getKeys(): string[] {
    return Array.from(this.data.keys());
  }

  public getOwner(): Actor | null {
    return this.owner;
  }

  public updateGameState(): void {
    if (!this.owner) return;

    const world = this.owner.getWorld();
    if (!world) return;

    // Update owner position
    const ownerPosition = new THREE.Vector3();
    this.owner.rootComponent.getWorldPosition(ownerPosition);
    this.setValue('SelfPosition', ownerPosition);

    // Find and update player information
    const playerPawn = world.getFirstPlayerPawn();
    if (playerPawn) {
      const playerPosition = new THREE.Vector3();
      playerPawn.rootComponent.getWorldPosition(playerPosition);
      this.setValue('PlayerPosition', playerPosition);
      this.setValue('PlayerActor', playerPawn);

      // Calculate distance to player
      const distanceToPlayer = ownerPosition.distanceTo(playerPosition);
      this.setValue('DistanceToPlayer', distanceToPlayer);
    } else {

    }

    // Update time information
    this.setValue('CurrentTime', world.getGameTime());
  }

  public getDebugInfo(): { [key: string]: any } {
    const debugInfo: { [key: string]: any } = {};
    for (const [key, value] of this.data.entries()) {
      if (value instanceof THREE.Vector3) {
        debugInfo[key] = `Vector3(${value.x.toFixed(2)}, ${value.y.toFixed(2)}, ${value.z.toFixed(2)})`;
      } else if (typeof value === 'object' && value?.name) {
        debugInfo[key] = `${value.constructor.name}(${value.name})`;
      } else {
        debugInfo[key] = value;
      }
    }
    return debugInfo;
  }
}